import { useState, useEffect } from "react";
import axios from "axios";
import InputForm from "@/components/InputForm";
import KPICards from "@/components/KPICards";
import RevenueChart from "@/components/RevenueChart";
import YearlyBreakdownTable from "@/components/YearlyBreakdownTable";
import { TrendUp } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const RevenueCalculator = () => {
  const [formData, setFormData] = useState({
    current_aum: 10000000,
    current_sip_book: 100000,
    monthly_lumpsum: 200000,
    monthly_sip: 50000,
    years_in_business: 10,
    redemption_rate: 5,
    market_growth_rate: 12,
    brokerage_rate: 0.7
  });

  const [calculationResult, setCalculationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const calculateRevenue = async (data) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${API}/calculate-revenue`, data);
      setCalculationResult(response.data);
    } catch (err) {
      console.error("Calculation error:", err);
      setError("Failed to calculate revenue. Please check your inputs.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    calculateRevenue(formData);
  }, []);

const handleFormChange = (newData) => {
    setFormData(newData);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      calculateRevenue(formData);
    }, 600);
    return () => clearTimeout(timer);
  }, [formData]);


  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-8 py-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-700 rounded-sm flex items-center justify-center">
              <TrendUp size={24} weight="duotone" className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold tracking-tight text-slate-900">
                MFD Trail Revenue Calculator
              </h1>
              <p className="text-sm text-slate-500 mt-0.5">Project your mutual fund distribution revenue</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[1600px] mx-auto px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left Column - Input Form (Sticky) */}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-24">
              <InputForm 
                formData={formData} 
                onChange={handleFormChange}
                loading={loading}
              />
            </div>
          </div>

          {/* Right Column - Results */}
          <div className="lg:col-span-8 space-y-8">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-sm p-4 text-red-800" data-testid="error-message">
                {error}
              </div>
            )}

            {calculationResult && (
              <>
                {/* KPI Cards */}
                <KPICards data={calculationResult} />

                {/* Revenue Chart */}
                <RevenueChart data={calculationResult.yearly_data} />

                {/* Yearly Breakdown Table */}
                <YearlyBreakdownTable data={calculationResult.yearly_data} />
              </>
            )}

            {loading && !calculationResult && (
              <div className="flex items-center justify-center py-20" data-testid="loading-indicator">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-700"></div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default RevenueCalculator;
